import { useCallback, useEffect, useMemo, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import {
  getClientDiagnostics,
  getSystemLog,
  listSystemLogs,
  type SystemLogFilter,
  type SystemLogItem,
} from '../features/system-logs';
import { formatDateTime } from '../lib/format';
import { useToastStore } from '../store/toast.store';

const LEVELS = ['', 'debug', 'info', 'warn', 'error', 'fatal'];
const SOURCES = ['', 'backend', 'admin_web', 'mobile'];
const PAGE_SIZE = 30;
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 50;

function levelClass(level: string): string {
  switch (level) {
    case 'error':
    case 'fatal':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'warn':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'info':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    default:
      return 'bg-gray-50 text-gray-600 border-gray-200';
  }
}

function sourceLabel(source: string): string {
  const map: Record<string, string> = {
    backend: '后端',
    admin_web: '管理端',
    mobile: '移动端',
  };
  return map[source] ?? source;
}

function errorText(error: unknown): string {
  if (isApiError(error)) return `请求失败 (${error.message})`;
  return '请求失败';
}

export function SystemLogsPage() {
  const addToast = useToastStore((s) => s.addToast);
  const [filter, setFilter] = useState<SystemLogFilter>({});
  const [draft, setDraft] = useState<SystemLogFilter>({});
  const [items, setItems] = useState<SystemLogItem[]>([]);
  const [selected, setSelected] = useState<SystemLogItem | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingDiagnostics, setDownloadingDiagnostics] = useState(false);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listSystemLogs(filter, page, PAGE_SIZE);
      setItems(result.items);
      setTotal(result.total);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilter = useCallback(() => {
    setPage(1);
    setSelected(null);
    setFilter(draft);
  }, [draft]);

  const resetFilter = useCallback(() => {
    setDraft({});
    setFilter({});
    setSelected(null);
    setPage(1);
  }, []);

  const openDetail = useCallback(async (item: SystemLogItem) => {
    setSelected(item);
    try {
      setSelected(await getSystemLog(item.id));
    } catch {
      setSelected(item);
    }
  }, []);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const all: SystemLogItem[] = [];
      for (let currentPage = 1; currentPage <= EXPORT_MAX_PAGES; currentPage += 1) {
        const result = await listSystemLogs(filter, currentPage, EXPORT_PAGE_SIZE);
        all.push(...result.items);
        if (all.length >= result.total || result.items.length === 0) break;
      }
      downloadCsv(all);
      addToast('success', `已导出 ${all.length} 条日志`);
    } catch (e) {
      addToast('error', errorText(e));
    } finally {
      setExporting(false);
    }
  }, [addToast, filter]);

  const detailData = useMemo(
    () => JSON.stringify(selected?.data ?? {}, null, 2),
    [selected],
  );
  const diagnosticsUploadId = useMemo(() => {
    const value = selected?.data?.upload_id;
    return typeof value === 'string' && value.trim() ? value.trim() : '';
  }, [selected]);

  const downloadDiagnostics = useCallback(async () => {
    if (!diagnosticsUploadId) return;
    setDownloadingDiagnostics(true);
    try {
      const payload = await getClientDiagnostics(diagnosticsUploadId);
      downloadJson(diagnosticsUploadId, payload);
      addToast('success', '诊断日志已下载');
    } catch (e) {
      addToast('error', errorText(e));
    } finally {
      setDownloadingDiagnostics(false);
    }
  }, [addToast, diagnosticsUploadId]);

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">系统日志</h2>
          <p className="text-sm text-gray-500 mt-1">
            汇总后端请求、数据库异常、管理端与移动端诊断上传摘要。
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void load()}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            刷新
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            className="px-3 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {exporting ? '导出中' : '导出 CSV'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-8 gap-3 bg-white border border-border rounded-lg p-4">
        <label className="space-y-1">
          <span className="text-xs text-gray-500">级别</span>
          <select
            value={draft.level ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, level: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
          >
            {LEVELS.map((level) => (
              <option key={level} value={level}>{level || '全部'}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">来源</span>
          <select
            value={draft.source ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, source: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
          >
            {SOURCES.map((source) => (
              <option key={source} value={source}>{source ? sourceLabel(source) : '全部'}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">分类</span>
          <input
            value={draft.category ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, category: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
            placeholder="http/database/diagnostics"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">请求 ID</span>
          <input
            value={draft.request_id ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, request_id: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
            placeholder="X-Request-Id"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">Session ID</span>
          <input
            value={draft.session_id ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, session_id: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
            placeholder="X-Session-Id"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">用户 ID</span>
          <input
            value={draft.user_id ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, user_id: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
            inputMode="numeric"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">关键词</span>
          <input
            value={draft.q ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, q: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
            placeholder="消息/事件/路径"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">开始日期</span>
          <input
            type="date"
            value={draft.since ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, since: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-500">结束日期</span>
          <input
            type="date"
            value={draft.until ?? ''}
            onChange={(e) => setDraft((s) => ({ ...s, until: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
          />
        </label>
        <div className="lg:col-span-2 flex items-end gap-2">
          <button
            type="button"
            onClick={applyFilter}
            className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800"
          >
            查询
          </button>
          <button
            type="button"
            onClick={resetFilter}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            重置
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4">
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex justify-between text-sm text-gray-500">
            <span>共 {total} 条</span>
            <span>{loading ? '加载中' : `第 ${page} / ${totalPages} 页`}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left font-medium px-4 py-3">时间</th>
                  <th className="text-left font-medium px-4 py-3">级别</th>
                  <th className="text-left font-medium px-4 py-3">来源</th>
                  <th className="text-left font-medium px-4 py-3">事件</th>
                  <th className="text-left font-medium px-4 py-3">请求</th>
                  <th className="text-left font-medium px-4 py-3">消息</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => void openDetail(item)}
                    className={`cursor-pointer hover:bg-gray-50 ${selected?.id === item.id ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatDateTime(item.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 border rounded text-xs ${levelClass(item.level)}`}>
                        {item.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{sourceLabel(item.source)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.event}</div>
                      <div className="text-xs text-gray-500">{item.category}</div>
                    </td>
                    <td className="px-4 py-3 max-w-[260px]">
                      <div className="text-xs text-gray-500">{item.method} {item.status_code ?? '-'}</div>
                      <div className="truncate text-gray-700">{item.path || '-'}</div>
                    </td>
                    <td className="px-4 py-3 max-w-[300px]">
                      <div className="truncate text-gray-700">{item.message || '-'}</div>
                      <div className="text-xs text-gray-400 truncate">{item.session_id || item.request_id || '-'}</div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                      暂无日志
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
            >
              上一页
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>

        <aside className="bg-white border border-border rounded-lg p-4 min-h-[420px]">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-gray-900">日志详情</h3>
            {diagnosticsUploadId && (
              <button
                type="button"
                onClick={() => void downloadDiagnostics()}
                disabled={downloadingDiagnostics}
                className="px-3 py-1.5 text-xs bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
              >
                {downloadingDiagnostics ? '下载中' : '下载诊断包'}
              </button>
            )}
          </div>
          {selected ? (
            <div className="mt-4 space-y-3 text-sm">
              <DetailRow label="ID" value={String(selected.id)} />
              <DetailRow label="时间" value={formatDateTime(selected.created_at)} />
              <DetailRow label="级别" value={selected.level} />
              <DetailRow label="来源" value={sourceLabel(selected.source)} />
              <DetailRow label="分类" value={selected.category} />
              <DetailRow label="事件" value={selected.event} />
              <DetailRow label="请求 ID" value={selected.request_id || '-'} />
              <DetailRow label="Session ID" value={selected.session_id || '-'} />
              <DetailRow label="用户 ID" value={selected.user_id ? String(selected.user_id) : '-'} />
              <DetailRow label="路径" value={selected.path || '-'} />
              <DetailRow label="耗时" value={selected.duration_ms === null ? '-' : `${selected.duration_ms} ms`} />
              <DetailRow label="IP" value={selected.ip || '-'} />
              <div>
                <div className="text-xs text-gray-500 mb-1">消息</div>
                <div className="text-gray-800 break-words">{selected.message || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">数据</div>
                <pre className="max-h-72 overflow-auto bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-700 whitespace-pre-wrap">
                  {detailData}
                </pre>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-500">选择左侧日志查看摘要数据。</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-gray-800 break-words">{value}</div>
    </div>
  );
}

function downloadCsv(items: SystemLogItem[]): void {
  const rows = [
    [
      'id', 'created_at', 'level', 'source', 'category', 'event', 'message',
      'request_id', 'session_id', 'user_id', 'method', 'path', 'status_code', 'duration_ms', 'ip', 'user_agent', 'data_json',
    ],
    ...items.map((item) => [
      String(item.id),
      item.created_at,
      item.level,
      item.source,
      item.category,
      item.event,
      item.message,
      item.request_id,
      item.session_id,
      item.user_id === null ? '' : String(item.user_id),
      item.method,
      item.path,
      item.status_code === null ? '' : String(item.status_code),
      item.duration_ms === null ? '' : String(item.duration_ms),
      item.ip,
      item.user_agent,
      JSON.stringify(item.data),
    ]),
  ];
  const csv = rows.map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `system-logs-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function downloadJson(uploadId: string, payload: unknown): void {
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${uploadId}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}
